import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { ApiClientError, api, postJson } from "../api";
import type { EventSummary, ParticipantMe } from "../api";
import { Brand } from "../components/Brand";
import { Spinner } from "../components/Spinner";
import {
  canRegisterParticipant,
  canSubmitEndStats,
} from "../eventAccess";

function formatEventTime(value: string, timezone: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: timezone,
    month: "long",
    day: "numeric",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function ParticipantPage({ slug }: { slug?: string }) {
  const [event, setEvent] = useState<EventSummary | null>(null);
  const [me, setMe] = useState<ParticipantMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  const [startStats, setStartStats] = useState("");
  const [startCustom, setStartCustom] = useState("");
  const [consent, setConsent] = useState(false);
  const [endStats, setEndStats] = useState("");
  const [endCustom, setEndCustom] = useState("");

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const participant = await api<ParticipantMe>("/api/participant/me");
      setMe(participant);
      setEvent(null);
    } catch (requestError) {
      if (requestError instanceof ApiClientError && requestError.status === 401) {
        try {
          setEvent(
            await api<EventSummary>(
              slug ? `/api/events/${encodeURIComponent(slug)}` : "/api/current-event",
            ),
          );
          setMe(null);
        } catch (eventError) {
          setError(eventError instanceof Error ? eventError.message : "活动信息加载失败");
        }
      } else {
        setError(requestError instanceof Error ? requestError.message : "状态加载失败");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [slug]);

  useEffect(() => {
    if (!me?.participant.qrUrl) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(me.participant.qrUrl, {
      width: 440,
      margin: 2,
      color: { dark: "#071411", light: "#f1fff9" },
      errorCorrectionLevel: "M",
    }).then((dataUrl) => {
      if (!cancelled) setQrDataUrl(dataUrl);
    });
    return () => {
      cancelled = true;
    };
  }, [me?.participant.qrUrl]);

  async function submitRegistration(eventObject: React.FormEvent) {
    eventObject.preventDefault();
    if (!event) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await postJson<ParticipantMe>(
        `/api/events/${encodeURIComponent(event.slug)}/register`,
        {
          rawStats: startStats,
          customStat: startCustom || null,
          consent,
        },
      );
      setMe(result);
      setEvent(null);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Start Stats 提交失败");
    } finally {
      setSubmitting(false);
    }
  }

  async function submitEnd(eventObject: React.FormEvent) {
    eventObject.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await postJson<ParticipantMe>(
        "/api/participant/end",
        { rawStats: endStats, customStat: endCustom || null },
      );
      setMe(result);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "End Stats 提交失败");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <main className="center-screen">
        <Brand />
        <Spinner label="正在连接活动控制台" />
      </main>
    );
  }

  return (
    <div className="participant-shell participant-shell--form">
      <header className="public-header">
        <Brand />
        <a className="text-link" href="/admin">
          组织者入口 ↗
        </a>
      </header>

      <main className="participant-main">
        {error && (
          <div className="alert alert--error">
            <span>{error}</span>
          </div>
        )}

        {!me && event && (
          <div className="simple-form">
            <section className="simple-form__title">
              <div className="eyebrow">{event.city} · INGRESS FIRST SATURDAY</div>
              <h1>{event.name}</h1>
              <p>{formatEventTime(event.startsAt, event.timezone)} 开始</p>
              <small>
                粘贴 Scanner Profile 的 All Time 数据。系统会自动识别 Codename 和
                Faction；最早一次提交固定作为 Start Stats。在其他设备再次提交时会自动恢复原记录。
              </small>
            </section>

            {canRegisterParticipant(event.state) &&
            Date.now() >= new Date(event.startsAt).getTime() ? (
              <form onSubmit={submitRegistration} className="simple-form__questions">
                <section className="simple-question">
                  <label htmlFor="scanner-profile-stats">
                    Scanner Profile Stats <strong aria-hidden="true">*</strong>
                  </label>
                  <p>
                    Ingress → Profile → All Time → 复制，然后在这里直接粘贴；也支持从
                    IM 转发后再次复制的常见格式。
                  </p>
                  <textarea
                    id="scanner-profile-stats"
                    value={startStats}
                    onChange={(input) => setStartStats(input.target.value)}
                    placeholder="在这里粘贴完整的 All Time Stats"
                    rows={10}
                    minLength={10}
                    required
                    spellCheck={false}
                  />
                  <small>Codename、Faction、Date 和 Time 将由系统自动读取。</small>
                </section>

                <section className="simple-question">
                  <label htmlFor="start-custom-stat">
                    Custom Stat <em>（可选）</em>
                  </label>
                  <input
                    id="start-custom-stat"
                    value={startCustom}
                    onChange={(input) => setStartCustom(input.target.value)}
                    placeholder="仅在组织者要求时填写"
                    maxLength={200}
                  />
                </section>

                <section className="simple-question simple-question--consent">
                  <label className="check-row">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(input) => setConsent(input.target.checked)}
                      required
                    />
                    <span>
                      我同意活动组织者保存从 Stats 自动识别的 Codename、Faction
                      和本次统计，并公开核验后的成绩。
                    </span>
                  </label>
                </section>

                <div className="simple-form__submit">
                  <button
                    className="button button--primary"
                    disabled={submitting}
                    type="submit"
                  >
                    {submitting ? "正在识别并写入…" : "提交"}
                  </button>
                  <span>* 必填</span>
                </div>
                <p className="simple-form__note">
                  活动开始前采集的 Stats 会被拒绝。已登记的 Codename 会恢复原记录，不会重复创建。
                </p>
              </form>
            ) : (
              <section className="simple-question simple-question--closed">
                <h2>
                  {Date.now() < new Date(event.startsAt).getTime()
                    ? "活动尚未开始"
                    : event.state === "draft" || event.state === "ready"
                      ? "签到尚未开放"
                      : "Stats 已停止接收"}
                </h2>
                <p>
                  {Date.now() < new Date(event.startsAt).getTime()
                    ? `请在 ${formatEventTime(event.startsAt, event.timezone)} 后重新复制并提交 Scanner Profile Stats。`
                    : event.state === "draft" || event.state === "ready"
                    ? "组织者完成 Sheet 检查并切换到 checkin 后即可提交。"
                    : "如需补录，请联系组织者处理。"}
                </p>
              </section>
            )}
          </div>
        )}

        {me && (
          <div className="simple-form participant-success">
            <section className="simple-form__title">
              <div className="eyebrow">
                {me.event.city} · {me.participant.faction}
              </div>
              <h1>{me.participant.codename}</h1>
              <small>{me.event.name}</small>
            </section>

            <section className="simple-question participant-pass">
              <h2>个人二维码</h2>
              {qrDataUrl ? (
                <img src={qrDataUrl} alt="个人签到二维码" />
              ) : (
                <Spinner label="正在生成二维码" />
              )}
              <p>向现场核验员出示此二维码。</p>
              {me.wallet.available && (
                <div className="wallet-choice">
                  <strong>选择你的 iOS 版本</strong>
                  <div className="wallet-choice__buttons">
                    <a
                      className="wallet-add-button"
                      href={me.wallet.downloadUrls.modern}
                    >
                      <span aria-hidden="true"></span>
                      iOS 27 及以后
                    </a>
                    <a
                      className="wallet-add-button wallet-add-button--fallback"
                      href={me.wallet.downloadUrls.fallback}
                    >
                      <span aria-hidden="true"></span>
                      iOS 26 及以前
                    </a>
                  </div>
                  <small>
                    可在“设置 → 通用 → 关于本机”查看 iOS 版本。
                  </small>
                </div>
              )}
            </section>

            <section className="simple-question participant-end">
              <h2>End Stats</h2>
              {!canSubmitEndStats(me.event.state) ? (
                <p>
                  {me.event.state === "locked" || me.event.state === "archived"
                    ? "End Stats 已锁定。"
                    : "End Stats 尚未开放。"}
                </p>
              ) : (
                <form className="simple-end-form" onSubmit={submitEnd}>
                  <label>
                    <span>
                      Scanner Profile Stats <strong aria-hidden="true">*</strong>
                    </span>
                    <small>
                      重新复制 All Time Stats。系统会核对身份，并保留最早提交作为 Start。
                    </small>
                    <textarea
                      rows={8}
                      value={endStats}
                      onChange={(input) => setEndStats(input.target.value)}
                      minLength={10}
                      required
                      spellCheck={false}
                      placeholder="在这里粘贴更新后的 All Time Stats"
                    />
                  </label>
                  <label>
                    <span>
                      Custom Stat <em>（可选）</em>
                    </span>
                    <input
                      value={endCustom}
                      onChange={(input) => setEndCustom(input.target.value)}
                      placeholder="仅在组织者要求时填写"
                      maxLength={200}
                    />
                  </label>
                  <button
                    className="button button--primary"
                    disabled={submitting}
                    type="submit"
                  >
                    {submitting
                      ? "正在识别并写入…"
                      : me.submissions.end
                        ? "更新 End Stats"
                        : "提交 End Stats"}
                  </button>
                </form>
              )}
            </section>
          </div>
        )}
      </main>
      <footer className="public-footer">
        <span>IFS FIELD SYSTEM / GOOGLE SHEET RECORD</span>
        <span>NO GPS TRACKING</span>
      </footer>
    </div>
  );
}
